/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, InputGroup, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import { useOrderWizardContext } from '../OrderWizardProvider';

const FinancialInformationStep = ({ currencies, validationErrors, onOpenAdminFeeModal }) => {
  const { orderData, updateOrderData } = useOrderWizardContext();

  const calculateNetInvestment = useCallback(() => {
    const gross = parseFloat(orderData.inversionBruta) || 0;
    const brokerFee = parseFloat(orderData.brokerFee) || 0;
    const mediaFee = parseFloat(orderData.mediaFee) || 0;
    const adminFee = parseFloat(orderData.feeAdmin) || 0;

    let brokerFeeAmount = 0;
    let mediaFeeAmount = 0;
    let adminFeeAmount = 0;

    // Calcular broker fee
    if (orderData.brokerFeeType === 'percentage') {
      brokerFeeAmount = (gross * brokerFee) / 100;
    } else {
      brokerFeeAmount = brokerFee;
    }

    // Calcular media fee
    if (orderData.mediaFeeType === 'percentage') {
      mediaFeeAmount = (gross * mediaFee) / 100;
    } else {
      mediaFeeAmount = mediaFee;
    }

    // Calcular admin fee
    if (orderData.feeAdminType === 'percentage') {
      adminFeeAmount = (gross * adminFee) / 100;
    } else {
      adminFeeAmount = adminFee;
    }

    const totalFees = brokerFeeAmount + mediaFeeAmount + adminFeeAmount;
    const netInvestment = Math.max(0, gross - totalFees);
    
    // *** ARREGLO: Usar batch updates para evitar múltiples re-renders ***
    updateOrderData({
      inversionNeta: netInvestment.toFixed(2),
      brokerFeeAmount: brokerFeeAmount.toFixed(2),
      mediaFeeAmount: mediaFeeAmount.toFixed(2),
      adminFeeAmount: adminFeeAmount.toFixed(2),
      totalFeesAmount: totalFees.toFixed(2)
    });
  }, [
    orderData.inversionBruta,
    orderData.brokerFee,
    orderData.brokerFeeType,
    orderData.mediaFee,
    orderData.mediaFeeType,
    orderData.feeAdmin,
    orderData.feeAdminType
  ]);


  useEffect(() => {
    if (orderData.inversionBruta) {
      calculateNetInvestment();
    }
  }, [
    orderData.inversionBruta,
    orderData.brokerFee,
    orderData.brokerFeeType,
    orderData.mediaFee,
    orderData.mediaFeeType,
    orderData.feeAdmin,
    orderData.feeAdminType,
    calculateNetInvestment
  ]);

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getFeeImpactColor = () => {
    const bruto = parseFloat(orderData.inversionBruta || 0);
    const neta = parseFloat(orderData.inversionNeta || 0);
    if (bruto === 0) return 'text-muted';
    
    const diff = ((bruto - neta) / bruto * 100);
    return diff > 15 ? 'text-danger' : diff > 8 ? 'text-warning' : 'text-success';
  };

  const getCurrencySymbol = () => {
    switch(orderData.moneda) {
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'USD':
      default: return '$';
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h5 className="text-primary mb-2">
          <FontAwesomeIcon icon="dollar-sign" className="me-2" />
          Información Financiera
        </h5>
        <p className="text-muted mb-0">
          Configura las fechas, moneda e inversiones de la orden
        </p>
      </div>

      <Row className="g-4">
        {/* Período de Campaña */}
        <Col xs={12}>
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0 text-warning">
              <FontAwesomeIcon icon="calendar" className="me-2" />
              Período de Campaña
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <FontAwesomeIcon icon="calendar-alt" className="me-1 text-success" />
                    Fecha de Inicio *
                  </Form.Label>
                  <DatePicker
                    selected={orderData.fechaDesde}
                    onChange={(date) => {
                      const time = date.toTimeString().slice(0, 5);
                      updateOrderData({ fechaDesde: date, horaDesde: time });
                    }}
                    className="form-control"
                    dateFormat="dd/MM/yyyy HH:mm"
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={1}
                    placeholderText="Seleccionar fecha de inicio"
                    required
                  />
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <FontAwesomeIcon icon="calendar-alt" className="me-1 text-danger" />
                    Fecha de Fin *
                  </Form.Label>
                  <DatePicker
                    selected={orderData.fechaHasta}
                    onChange={(date) => {
                      const time = date.toTimeString().slice(0, 5);
                      updateOrderData({ fechaHasta: date, horaHasta: time });
                    }}
                    className="form-control"
                    dateFormat="dd/MM/yyyy HH:mm"
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={1}
                    placeholderText="Seleccionar fecha de fin"
                    minDate={orderData.fechaDesde}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      {/* Inversión y Moneda */}
      <Col xs={12}>
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0 text-warning">
              <FontAwesomeIcon icon="dollar-sign" className="me-2" />
              Inversión y Moneda
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col lg={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Moneda *
                  </Form.Label>
                  <Form.Select
                    value={orderData.moneda}
                    onChange={(e) => updateOrderData('moneda', e.target.value)}
                    required
                  >
                    {currencies.map(currency => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Moneda de la inversión
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col lg={8}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Inversión Bruta *
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>{getCurrencySymbol()}</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={orderData.inversionBruta}
                      onChange={(e) => updateOrderData('inversionBruta', e.target.value)}
                      placeholder="10000"
                      className={validationErrors.inversionBruta ? 'is-invalid' : ''}
                      required
                    />
                  </InputGroup>
                  {validationErrors.inversionBruta && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.inversionBruta}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Monto total antes de fees y comisiones
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      {/* Fees y Comisiones */}
      <Col xs={12}>
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0 text-warning">
              <FontAwesomeIcon icon="percentage" className="me-2" />
              Fees y Comisiones
            </h6>
          </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col lg={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon="handshake" className="me-1 text-info" />
                  Broker Fee
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={orderData.brokerFee}
                    onChange={(e) => updateOrderData('brokerFee', e.target.value)}
                    placeholder="5"
                  />
                  <Form.Select
                    value={orderData.brokerFeeType}
                    onChange={(e) => updateOrderData('brokerFeeType', e.target.value)}
                    style={{ maxWidth: '80px' }}
                  >
                    <option value="percentage">%</option>
                    <option value="fixed">{orderData.moneda}</option>
                  </Form.Select>
                </InputGroup>
                <Form.Text className="text-muted">
                  Comisión del broker (opcional)
                </Form.Text>
                {orderData.brokerFeeAmount && parseFloat(orderData.brokerFeeAmount) > 0 && (
                  <div className="mt-1">
                    <small className="text-info fw-bold">
                      = {formatCurrency(orderData.brokerFeeAmount, orderData.moneda)}
                    </small>
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col lg={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon="tv" className="me-1 text-info" />
                  Media Fee
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={orderData.mediaFee}
                    onChange={(e) => updateOrderData('mediaFee', e.target.value)}
                    placeholder="15"
                  />
                  <Form.Select
                    value={orderData.mediaFeeType}
                    onChange={(e) => updateOrderData('mediaFeeType', e.target.value)}
                    style={{ maxWidth: '80px' }}
                  >
                    <option value="percentage">%</option>
                    <option value="fixed">{orderData.moneda}</option>
                  </Form.Select>
                </InputGroup>
                <Form.Text className="text-muted">
                  Fee de medios (opcional)
                </Form.Text>
                {orderData.mediaFeeAmount && parseFloat(orderData.mediaFeeAmount) > 0 && (
                  <div className="mt-1">
                    <small className="text-info fw-bold">
                      = {formatCurrency(orderData.mediaFeeAmount, orderData.moneda)}
                    </small>
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col lg={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon="cogs" className="me-1 text-warning" />
                  Fee Administrativo
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={orderData.feeAdmin}
                    onChange={(e) => updateOrderData('feeAdmin', e.target.value)}
                    placeholder="3.5"
                    readOnly
                  />
                  <Form.Select
                    value={orderData.feeAdminType}
                    onChange={(e) => updateOrderData('feeAdminType', e.target.value)}
                    style={{ maxWidth: '80px' }}
                    disabled
                  >
                    <option value="percentage">%</option>
                    <option value="fixed">{orderData.moneda}</option>
                  </Form.Select>
                  <Button
                    variant="outline-warning"
                    onClick={onOpenAdminFeeModal}
                    title="Configurar fee administrativo"
                  >
                    <FontAwesomeIcon icon="cog" />
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Usa el botón de configuración para seleccionar un fee predefinido
                </Form.Text>
                {orderData.adminFeeAmount && parseFloat(orderData.adminFeeAmount) > 0 && (
                  <div className="mt-1">
                    <small className="text-warning fw-bold">
                      = {formatCurrency(orderData.adminFeeAmount, orderData.moneda)}
                    </small>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      </Col>

      {/* Resumen Financiero Detallado */}
      <Col xs={12}>
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0 text-warning">
              <FontAwesomeIcon icon="calculator" className="me-2" />
              Resumen Financiero
            </h6>
          </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col lg={3} md={6}>
              <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                <div className="text-muted small mb-1">Inversión Bruta</div>
                <div className="h5 fw-bold text-success mb-0">
                  {formatCurrency(orderData.inversionBruta, orderData.moneda)}
                </div>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                <div className="text-muted small mb-1">Total Fees</div>
                <div className="h5 fw-bold text-warning mb-0">
                  {formatCurrency(orderData.totalFeesAmount, orderData.moneda)}
                </div>
                <div className="small text-muted">
                  {orderData.brokerFeeAmount && parseFloat(orderData.brokerFeeAmount) > 0 && (
                    <div>Broker: {formatCurrency(orderData.brokerFeeAmount, orderData.moneda)}</div>
                  )}
                  {orderData.mediaFeeAmount && parseFloat(orderData.mediaFeeAmount) > 0 && (
                    <div>Media: {formatCurrency(orderData.mediaFeeAmount, orderData.moneda)}</div>
                  )}
                  {orderData.adminFeeAmount && parseFloat(orderData.adminFeeAmount) > 0 && (
                    <div>Admin: {formatCurrency(orderData.adminFeeAmount, orderData.moneda)}</div>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                <div className="text-muted small mb-1">Inversión Neta</div>
                <div className="h5 fw-bold text-primary mb-0">
                  {formatCurrency(orderData.inversionNeta, orderData.moneda)}
                </div>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className="text-center p-3 bg-light rounded">
                <div className="text-muted small mb-1">Impacto</div>
                <div className={`h5 fw-bold mb-0 ${getFeeImpactColor()}`}>
                  {orderData.inversionBruta && orderData.inversionNeta ? 
                    `${(((parseFloat(orderData.inversionBruta) - parseFloat(orderData.inversionNeta)) / parseFloat(orderData.inversionBruta)) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      </Col>
      </Row>

      {/* Información de Progreso */}
      <Col xs={12}>
        <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <small className="text-muted">Paso 2 de 4</small>
            </div>
            <div className="text-end">
              <small className="text-primary">
                <FontAwesomeIcon icon="arrow-right" className="me-1" />
                Siguiente: Configuración Avanzada
              </small>
            </div>
          </div>
        </div>
      </Col>
    </div>
  );
};

FinancialInformationStep.propTypes = {
  currencies: PropTypes.array.isRequired,
  validationErrors: PropTypes.object,
  onOpenAdminFeeModal: PropTypes.func
};

export default FinancialInformationStep; 